import React from "react";
import Select, { components } from "react-select";
import { useController, Control } from "react-hook-form";
import "./controlled-select.css";
import { OptionType, useSelectValue } from "./select-value-context";
import { CheckIcon } from "./check-icon";
import CheckboxFilledIcon from "./checkbox-filled-icon";
import CheckboxEmptyIcon from "./checkbox-empty-icon";

interface Props {
    name: string;
    // @ts-ignore TODO: Target. React form complaints when typing with string[]
    control: Control<any>;
    required?: boolean;
    isMulti?: boolean;
    id?: string;
    testId?: string;
    isSearchable?: boolean;
    removeUnavailableOptions?: boolean;
    // We cant use generics yet for the option because it comes from the provider
    // TODO improve option type by using generics
    onChange?: (option: any, actionMeta: any) => void;
    placeholder?: null | string | number | boolean | {} | React.ReactElement | React.ReactNodeArray | React.ReactPortal;
}

export const ControlledSelect = ({
    name,
    control,
    required,
    isMulti = false,
    isSearchable = false,
    removeUnavailableOptions,
    onChange: onChangeProp,
    ...rest
}: Props) => {
    const {
        field: { value, onChange, onBlur },
    } = useController({
        name,
        control,
        defaultValue: [],
        rules: { required },
    });

    const { options } = useSelectValue() ?? {};

    // Convert plain string into format React select wants
    const valuesForComponent = isMulti
        ? value.map((val) => {
              const foundOption = options.find((option) => option.value === val);
              if (foundOption) {
                  return {
                      // `options` will have formatted label after making executesql. So lookup that label by key
                      label:
                          options.length === 0 ? val : (options.find((option) => option.value === val)?.label ?? val),
                      value: val,
                  };
              }
          })
        : options.find((option) => option.value === value);

    const handleChange = (option, actionMeta) => {
        // Handling simple select change
        if (!isMulti) {
            onChange(option.value);
            if (onChangeProp) {
                onChangeProp(option, actionMeta);
            }
            return;
        }

        // Handling multi select change
        const selectedOption = actionMeta.option;
        const valueExists = value.find((currentValue) => currentValue === selectedOption?.value);
        const { removedValue } = actionMeta;

        if (valueExists || removedValue) {
            onChange(value.filter((v) => v !== (selectedOption?.value ?? removedValue.value)));
            return;
        }
        if (actionMeta.action === "clear") {
            onChange([]);
            return;
        }
        onChange([...value, selectedOption.value]);
        if (onChangeProp) {
            onChangeProp(option, actionMeta);
        }
    };

    const handleBlur = () => {
        onBlur();
    };

    const getOptionLabel = (option: OptionType) => {
        if (!isMulti) {
            return <span>{option.label ?? option}</span>;
        }

        return (
            <div key={option.value} style={{ display: "flex", alignItems: "center", columnGap: "12px" }}>
                {value.find((v) => option.value === v) ? <CheckboxFilledIcon /> : <CheckboxEmptyIcon />}
                <span>{option.label}</span>
            </div>
        );
    };

    // Filter out any values not in the values for component
    React.useEffect(() => {
        if (removeUnavailableOptions) {
            const filtered = [];
            let triggerChange = false;
            for (const v of value) {
                if (!options.find((option) => option.value === v)) {
                    triggerChange = true;
                } else {
                    filtered.push(v);
                }
            }
            if (triggerChange) {
                onChange(filtered);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeUnavailableOptions]);

    return (
        <Select
            {...rest}
            styles={customStyles}
            options={options}
            value={valuesForComponent}
            onChange={handleChange}
            onBlur={handleBlur}
            isMulti={isMulti}
            hideSelectedOptions={false}
            closeMenuOnSelect={!isMulti}
            menuPosition="fixed"
            components={{ MultiValue, Option }}
            isSearchable={isSearchable}
            formatOptionLabel={getOptionLabel}
        />
    );
};

const MultiValue = React.memo((props: { data: OptionType }) => {
    const { data } = props;

    return (
        <div className="multi-value">
            <components.MultiValue {...props}>
                <span className="multi-value-label">{data.label}</span>
            </components.MultiValue>
        </div>
    );
});

export const Option = React.memo((props: { children: React.ReactNode; [key: string]: any }) => {
    const { children, ...rest } = props;
    return (
        <components.Option {...rest}>
            <div className="controlled-select-option-wrapper">
                <div>{children}</div>
                {!rest.isMulti && <div>{rest.isSelected && <CheckIcon />}</div>}
            </div>
        </components.Option>
    );
});

export const customStyles = {
    option: (provided, state) => {
        return {
            ...provided,
            backgroundColor:
                state.isFocused || state.isHovered
                    ? "rgba(88, 88, 88, 0.06)"
                    : state.isSelected
                      ? "white"
                      : provided.backgroundColor,
            color: "black",
            ":active": {
                backgroundColor: "rgba(88, 88, 88, 0.18)",
            },
        };
    },
    indicatorSeparator: () => ({ display: "none" }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: "#E1E1E1",
        color: "white",
        borderRadius: "30px",
        ":hover": {
            borderRadius: "30px",
        },
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        color: "rgb(96, 94, 92)",
        ":hover": {
            color: "black",
        },
    }),
    container: (provided) => ({
        ...provided,
        ":focus": {
            borderColor: "#00a9a4",
            boxShadow: "none",
        },
    }),
    control: (provided, state) => ({
        ...provided,
        border: state.isFocused ? "1px solid #00a9a4" : "1px solid #bdbdbd",
        boxShadow: "none",
        ":hover": {
            border: "1px solid black",
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        maxHeight: "95px",
        overflowY: "scroll",
    }),
};
